
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, CheckCircle, XCircle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { format } from "date-fns";
import { useAuthSession } from "@/hooks/useAuthSession";
import { toast } from "sonner";
import { Question } from "@/types/quiz";

interface QuizResult {
  id: string;
  user_id: string;
  session_id: string;
  score: number;
  total_questions: number;
  percentage: number;
  difficulty: string;
  date_filter?: string;
  time_period?: string;
  completed_at: string;
  questions?: Question[];
  answers?: (number | null)[];
}

const CurrentAffairsQuizResult: React.FC = () => {
  const { resultId } = useParams<{ resultId: string }>();
  const navigate = useNavigate();
  const { userId } = useAuthSession();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<QuizResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchResult = async () => {
      try {
        setLoading(true);

        if (!userId) {
          setError("You must be logged in to view this result");
          return;
        }

        if (!resultId) {
          setError("No result ID provided");
          return;
        }

        const { data, error: fetchError } = await supabase
          .from("current_affairs_quiz_results")
          .select("*")
          .eq("id", resultId)
          .eq("user_id", userId)
          .single();

        if (fetchError) {
          throw fetchError;
        }

        if (data) {
          setResult(data as QuizResult);
        } else {
          setError("Result not found or you don't have permission to view it");
        }
      } catch (err) {
        console.error("Error fetching quiz result:", err);
        setError("Failed to load result details");
        toast.error("Failed to load result details");
      } finally {
        setLoading(false);
      }
    };

    fetchResult();
  }, [resultId, userId]);

  if (error) {
    return (
      <div className="container mx-auto p-4 max-w-4xl">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          size="sm"
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <Card className="border-red-200">
          <CardContent className="pt-6 flex flex-col items-center text-center">
            <XCircle className="h-12 w-12 text-red-500 mb-4" />
            <h2 className="text-2xl font-bold mb-2">Error Loading Result</h2>
            <p className="text-gray-500 mb-4">{error}</p>
            <Button onClick={() => navigate("/current-affairs/analytics")}>
              Go to Analytics
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading || !result) {
    return (
      <div className="container mx-auto p-4 max-w-4xl">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  const questionsWithAnswers = result.questions?.map((question, index) => ({
    ...question,
    selectedAnswer: result.answers?.[index] ?? null,
    isCorrect: result.answers?.[index] === question.correctanswer,
  }));

  return (
    <div className="container mx-auto p-4 max-w-4xl">
      <Button
        variant="ghost"
        onClick={() => navigate(-1)}
        size="sm"
        className="mb-4"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back
      </Button>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Quiz Result Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              <div className="flex justify-center">
                <div className="relative w-40 h-40">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <svg className="w-full h-full" viewBox="0 0 100 100">
                      <circle
                        className="text-gray-200"
                        strokeWidth="8"
                        stroke="currentColor"
                        fill="transparent"
                        r="40"
                        cx="50"
                        cy="50"
                      />
                      <circle
                        className={`${
                          result.percentage >= 70
                            ? "text-green-500"
                            : result.percentage >= 40
                            ? "text-yellow-500"
                            : "text-red-500"
                        }`}
                        strokeWidth="8"
                        strokeDasharray={`${result.percentage * 2.51} 251`}
                        strokeLinecap="round"
                        stroke="currentColor"
                        fill="transparent"
                        r="40"
                        cx="50"
                        cy="50"
                        transform="rotate(-90 50 50)"
                      />
                    </svg>
                    <span className="absolute text-3xl font-bold">
                      {result.percentage}%
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-center mt-4">
                <div className="bg-gray-50 p-3 rounded-md">
                  <p className="text-sm text-gray-500">Correct Answers</p>
                  <p className="text-2xl font-bold text-green-600">
                    {result.score} / {result.total_questions}
                  </p>
                </div>
                <div className="bg-gray-50 p-3 rounded-md">
                  <p className="text-sm text-gray-500">Difficulty</p>
                  <p className="text-2xl font-bold capitalize">{result.difficulty}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-md">
                  <p className="text-sm text-gray-500">Date Completed</p>
                  <p className="text-lg font-medium">
                    {format(new Date(result.completed_at), "PPP")}
                  </p>
                </div>
                <div className="bg-gray-50 p-3 rounded-md">
                  <p className="text-sm text-gray-500">Time Period</p>
                  <p className="text-lg font-medium capitalize">
                    {result.time_period || "Not specified"}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Question Review</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {questionsWithAnswers?.map((question, index) => (
                <div
                  key={index}
                  className={`border rounded-md p-4 ${
                    question.isCorrect
                      ? "bg-green-50 border-green-200"
                      : "bg-red-50 border-red-200"
                  }`}
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mr-3">
                      {question.isCorrect ? (
                        <CheckCircle className="h-6 w-6 text-green-500" />
                      ) : (
                        <XCircle className="h-6 w-6 text-red-500" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-medium text-lg">
                        Q{index + 1}: {question.question}
                      </h3>
                      <div className="mt-2 space-y-2">
                        {question.options.map((option, optIndex) => (
                          <div
                            key={optIndex}
                            className={`p-2 rounded ${
                              optIndex === question.selectedAnswer &&
                              optIndex === question.correctanswer
                                ? "bg-green-200"
                                : optIndex === question.selectedAnswer
                                ? "bg-red-200"
                                : optIndex === question.correctanswer
                                ? "bg-green-100"
                                : "bg-gray-100"
                            }`}
                          >
                            <span className="font-medium">{option}</span>
                            {optIndex === question.selectedAnswer && (
                              <span className="ml-2 text-sm">
                                (Your answer)
                              </span>
                            )}
                            {optIndex === question.correctanswer && (
                              <span className="ml-2 text-sm text-green-700">
                                (Correct answer)
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                      {question.explanation && (
                        <div className="mt-3 text-sm bg-white p-3 border rounded">
                          <span className="font-medium">Explanation: </span>
                          {question.explanation}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-between">
              <Button
                variant="outline"
                onClick={() => navigate("/current-affairs/quiz")}
              >
                Take Another Quiz
              </Button>
              <Button
                onClick={() => navigate("/current-affairs/analytics")}
              >
                View Analytics
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CurrentAffairsQuizResult;
